import { Component, OnInit, AfterViewInit, ViewChild, TemplateRef } from '@angular/core';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError, map, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';
import { LeftMenuComponent } from '../../menu/left-menu/left-menu.component';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { PlatformMasterService, PlatformMasterDto, PlatformUserRecord } from '../../services/platform-master.service';
import { DataGridComponent, GridColumn, GridConfig, GridAction } from '../../shared/data-grid/data-grid.component';
import { AssignmentService, UserSearchResult } from '../../applications/Assignment/assignment.service';
import { MessageDialogComponent, MessageDialogData } from '../../shared/message-dialog/message-dialog.component';
import { PlatformMasterSkeletonComponent } from './platform-master-skeleton/platform-master-skeleton.component';

@Component({
  selector: 'modernization-platform-master',
  templateUrl: './platform-master.component.html',
  styleUrls: ['./platform-master.component.css'],
  imports: [FormsModule, ReactiveFormsModule, CommonModule, MaterialModule, LeftMenuComponent, SpinnerComponent, DataGridComponent, PlatformMasterSkeletonComponent]
})
export class PlatformMasterComponent implements OnInit, AfterViewInit {
  @ViewChild('functionCellTemplate') functionCellTemplate!: TemplateRef<any>;

  pageLoading = false;
  isLoading = false;
  isGridLoading = false;
  showForm = false;
  successMessage: string | null = null;
  errorMessage: string | null = null;
  infoMessage: string | null = null;

  // Inline edit state
  editingRow: any = null;
  editingFunction = '';
  isSaving = false;

  // Server-side pagination state
  totalPlatformRecords = 0;
  currentPage = 1;
  currentPageSize = 10;

  // Server-side filter state
  emailFilter = '';
  functionFilter = '';

  // Grid filter options (populated from digitalPlatforms after load)
  gridFilterOptions: { [column: string]: string[] } = {};
  filtersLoaded = false;
  private initialPageRequestsPending = 0;

  platformData: PlatformMasterDto = {
    operationManagerEmail: '',
    operationManagerName: '',
    function: ''
  };

  // User email autocomplete
  emailControl = new FormControl('');
  filteredUsers$!: Observable<UserSearchResult[]>;
  showSuggestions = false;
  emailSelectedFromSearch = false;

  platformList: PlatformUserRecord[] = [];
  digitalPlatforms: string[] = [];

  platformGridColumns: GridColumn[] = [
    { key: 'operationManagerEmail', label: 'Platform Master Email', sortable: true },
    { key: 'function', label: 'Function', sortable: true }
  ];

  platformGridActions: GridAction[] = [
    {
      label: 'Edit',
      tooltip: 'Edit',
      visible: (item) => this.editingRow !== item,
      disabled: () => this.editingRow !== null
    },
    {
      label: 'Delete',
      tooltip: 'Delete',
      color: 'danger',
      visible: (item) => this.editingRow !== item,
      disabled: () => this.editingRow !== null || this.isSaving
    },
    {
      label: 'Save',
      tooltip: 'Save',
      visible: (item) => this.editingRow === item,
      disabled: () => this.isSaving
    },
    {
      label: 'Cancel',
      tooltip: 'Cancel',
      visible: (item) => this.editingRow === item,
      disabled: () => this.isSaving
    }
  ];

  platformGridConfig: GridConfig = {
    pageSize: 10,
    pageSizeOptions: [5, 10, 25, 50],
    enableSorting: true,
    showColumnFilters: true,
    stickyHeader: true
  };

  constructor(
    private platformMasterService: PlatformMasterService,
    private assignmentService: AssignmentService,
    private dialog: MatDialog
  ) { }

  ngAfterViewInit(): void {
    this.platformGridColumns = this.platformGridColumns.map(col =>
      col.key === 'function'
        ? { ...col, customTemplate: this.functionCellTemplate }
        : col
    );
  }

  ngOnInit(): void {
    this.beginInitialPageLoad(2);
    this.loadPlatforms(true);
    this.loadDigitalPlatforms(true);
    this.filteredUsers$ = this.emailControl.valueChanges.pipe(
      tap(() => { this.emailSelectedFromSearch = false; }),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        const searchTerm = typeof value === 'string' ? value : '';
        this.platformData.operationManagerEmail = searchTerm;
        this.platformData.operationManagerName = '';
        if (searchTerm && searchTerm.length >= 3) {
          return this.assignmentService.searchUsers(searchTerm).pipe(
            map(response => response.value || []),
            catchError(() => of([]))
          );
        }
        return of([]);
      })
    );
  }

  private beginInitialPageLoad(requests: number): void {
    this.initialPageRequestsPending = requests;
    this.pageLoading = true;
  }

  private completeInitialPageLoad(): void {
    this.initialPageRequestsPending = Math.max(0, this.initialPageRequestsPending - 1);
    if (this.initialPageRequestsPending === 0) {
      this.pageLoading = false;
    }
  }

  onUserSelected(user: UserSearchResult): void {
    this.platformData.operationManagerEmail = user.mail;
    this.platformData.operationManagerName = user.displayName;
    this.emailControl.setValue(user.mail, { emitEvent: false });
    this.emailSelectedFromSearch = true;
    this.showSuggestions = false;
  }

  onEmailBlur(): void {
    setTimeout(() => { this.showSuggestions = false; }, 150);
  }

  loadDigitalPlatforms(isInitialLoad = false): void {
    this.platformMasterService.getDigitalPlatforms().subscribe({
      next: (data) => {
        this.digitalPlatforms = data.digitalPlatforms ?? [];
        this.gridFilterOptions = { function: data.digitalPlatforms ?? [] };
        this.infoMessage = data.infoMessage;
        this.filtersLoaded = true;
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        }
      },
      error: () => {
        this.digitalPlatforms = [];
        this.filtersLoaded = true;
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        }
      }
    });
  }

  loadPlatforms(isInitialLoad = false): void {
    if (!isInitialLoad) {
      this.isGridLoading = true;
    }
    this.platformMasterService.getAllPlatformUsers({
      page: this.currentPage,
      pageSize: this.currentPageSize,
      email: this.emailFilter || undefined,
      function: this.functionFilter || undefined
    }).subscribe({
      next: (response) => {
        this.platformList = response.data ?? [];
        this.totalPlatformRecords = response.totalRecords ?? 0;
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        } else {
          this.isGridLoading = false;
        }
      },
      error: () => {
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        } else {
          this.isGridLoading = false;
        }
      }
    });
  }

  onPageChanged(event: { pageIndex: number; pageSize: number }): void {
    this.currentPage = event.pageIndex + 1;
    this.currentPageSize = event.pageSize;
    this.loadPlatforms();
  }

  onColumnFilterChanged(event: { column: string; value: string }): void {
    if (event.column === 'operationManagerEmail') {
      this.emailFilter = event.value ?? '';
    } else if (event.column === 'function') {
      // multi-select values joined with \u001F separator — convert to comma-separated
      this.functionFilter = event.value
        ? event.value.split('\u001F').filter(v => v.trim()).join(',')
        : '';
    }
    // Reset to first page on filter change
    this.currentPage = 1;
    this.loadPlatforms();
  }

  onSortChanged(sortState: { column: string; direction: 'asc' | 'desc' | '' }): void {
    if (sortState.direction) {
      this.platformList = [...this.platformList].sort((a, b) => {
        const aValue = ((a as any)[sortState.column] ?? '').toString().toLowerCase();
        const bValue = ((b as any)[sortState.column] ?? '').toString().toLowerCase();
        const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        return sortState.direction === 'asc' ? comparison : -comparison;
      });
    }
  }

  toggleForm(): void {
    this.showForm = true;
  }

  onSubmit(): void {
    if (!this.platformData.operationManagerEmail || !this.platformData.function) {
      this.errorMessage = 'All fields are required.';
      this.successMessage = null;
      return;
    }

    if (!this.emailSelectedFromSearch) {
      this.errorMessage = 'Please select a user from the suggestions.';
      this.successMessage = null;
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;

    this.platformMasterService.addPlatform(this.platformData).subscribe({
      next: (_) => {
        this.isLoading = false;
        this.successMessage = 'Platform added successfully!';
        this.errorMessage = null;
        this.showForm = false;
        // Reset form
        this.platformData = {
          operationManagerEmail: '',
          operationManagerName: '',
          function: ''
        };
        this.emailControl.setValue('', { emitEvent: false });
        this.emailSelectedFromSearch = false;
        this.loadPlatforms();
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = error.error?.title || 'Failed to add platform. Please try again.';
        this.successMessage = null;
      }
    });
  }

  onCancel(): void {
    this.showForm = false;
    this.platformData = {
      operationManagerEmail: '',
      operationManagerName: '',
      function: ''
    };
    this.emailControl.setValue('', { emitEvent: false });
    this.emailSelectedFromSearch = false;
    this.errorMessage = null;
    this.successMessage = null;
  }

  // ── Grid inline edit / delete ─────────────────────────────────────────

  onActionClicked(event: { action: GridAction; item: any; event: Event }): void {
    switch (event.action.label) {
      case 'Edit': this.startEdit(event.item); break;
      case 'Delete': this.confirmDelete(event.item); break;
      case 'Save': this.saveEdit(); break;
      case 'Cancel': this.cancelEdit(); break;
    }
  }

  startEdit(item: any): void {
    this.editingRow = item;
    this.editingFunction = '';
    this.errorMessage = null;
    this.successMessage = null;
  }

  get availableFunctions(): string[] {
    return this.digitalPlatforms.filter(p => p !== this.editingRow?.function);
  }

  cancelEdit(): void {
    this.editingRow = null;
    this.editingFunction = '';
  }

  saveEdit(): void {
    if (!this.editingFunction) {
      this.openMessageDialog('Validation Error', 'Please select a function.', 'error');
      return;
    }
    this.isSaving = true;
    const id = this.editingRow.id;
    this.platformMasterService.updatePlatformUser(id, { function: this.editingFunction }).subscribe({
      next: () => {
        this.isSaving = false;
        this.editingRow = null;
        this.editingFunction = '';
        this.loadPlatforms();
        this.openMessageDialog('Success', 'Platform updated successfully!', 'success');
      },
      error: (error) => {
        this.isSaving = false;
        this.openMessageDialog('Error', error.error?.message || 'Failed to update platform. Please try again.', 'error');
      }
    });
  }

  confirmDelete(item: any): void {
    const ref = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you want to delete "${item.operationManagerEmail}, ${item.function}"?`,
        confirmMode: true,
        confirmLabel: 'Delete'
      } as MessageDialogData,
      disableClose: true
    });

    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.isSaving = true;
      const id = item.id;
      this.platformMasterService.deletePlatformUser(id).subscribe({
        next: () => {
          this.isSaving = false;
          this.loadPlatforms();
          this.openMessageDialog('Success', 'Platform deleted successfully!', 'success');
        },
        error: (error) => {
          this.isSaving = false;
          this.openMessageDialog('Error', error.error?.message || 'Failed to delete platform. Please try again.', 'error');
        }
      });
    });
  }

  private openMessageDialog(title: string, message: string, type?: 'success' | 'error' | 'info'): void {
    this.dialog.open(MessageDialogComponent, {
      width: '420px',
      data: { title, message, type } as MessageDialogData,
      disableClose: true
    });
  }
}
